import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { ChevronLeft, Download, Check, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import ShareToolkitDialog from "../components/ShareToolkitDialog";
import SuggestedTracks from "../components/SuggestedTracks";

// Debounced auto-save reflection component
const ReflectionField = ({ toolkitId, index, initial, onSaved }) => {
  const [text, setText] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);
  const firstRun = useRef(true);

  useEffect(() => {
    setText(initial || "");
    // eslint-disable-next-line
  }, [initial]);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.post(`/toolkit/${toolkitId}/reflection`, {
          section_index: index,
          text,
        });
        setSavedAt(data.updated_at);
        onSaved?.(index, text);
      } catch (e) {
        toast.error("Couldn't save reflection");
      } finally {
        setSaving(false);
      }
    }, 900);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line
  }, [text]);

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-widest text-teal-300/80">Your reflection</label>
        <span className="text-xs text-neutral-500" data-testid={`reflection-status-${index}`}>
          {saving ? "Saving…" : savedAt ? "Saved" : text ? "Unsaved changes…" : "Private to you"}
        </span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write here. It saves as you type."
        rows={4}
        data-testid={`reflection-input-${index}`}
        className="wwr-input w-full rounded-xl px-4 py-3 text-sm resize-none"
      />
    </div>
  );
};

const ToolkitViewer = () => {
  const { id } = useParams();
  const [toolkit, setToolkit] = useState(null);
  const [err, setErr] = useState(null);
  const [reflections, setReflections] = useState({});
  const [progress, setProgress] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const [t, r, p] = await Promise.all([
          api.get(`/toolkit/${id}`),
          api.get(`/toolkit/${id}/reflections`),
          api.get(`/toolkit/${id}/progress`),
        ]);
        setToolkit(t.data);
        setReflections(r.data);
        setProgress(p.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load toolkit");
        toast.error("This toolkit is locked");
      }
    })();
  }, [id]);

  // Jump to last section user left off on (only on first load)
  const jumpedRef = useRef(false);
  useEffect(() => {
    if (jumpedRef.current || !toolkit || !progress) return;
    const idx = progress.last_section_index || 0;
    if (idx > 0 && sectionRefs.current[idx]) {
      jumpedRef.current = true;
      setTimeout(() => sectionRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [toolkit, progress]);

  const markRead = async (index) => {
    try {
      const { data } = await api.post(`/toolkit/${id}/progress`, { section_index: index });
      setProgress({ ...data });
    } catch {}
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("wwr_token");
      const res = await fetch(`${API}/toolkit/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(toolkit?.title || "toolkit").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded — yours to keep.");
    } catch {
      toast.error("Could not download PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl mb-4">Locked</h1>
        <p className="text-neutral-400 mb-8">{err}</p>
        <Link to="/studio" className="btn-primary px-5 py-2">Back to The Studio</Link>
      </div>
    );
  }

  if (!toolkit) return <div className="p-12 text-center text-teal-200/70">Loading…</div>;

  const pct = progress?.progress_pct ?? 0;
  const sectionsRead = new Set(progress?.sections_read || []);

  return (
    <div className="fade-in max-w-3xl mx-auto px-6 py-16">
      <Link to="/dashboard" className="text-sm text-teal-300 hover:text-teal-200 inline-flex items-center gap-1 mb-8" data-testid="toolkit-back-link">
        <ChevronLeft size={16} /> Back to dashboard
      </Link>

      <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
        <div className="flex-1 min-w-[240px]">
          <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">Interactive Toolkit</div>
          <h1 className="font-display text-5xl mb-3">{toolkit.title}</h1>
          {toolkit.subtitle && (
            <p className="font-display text-xl text-teal-200/80 mb-2">{toolkit.subtitle}</p>
          )}
        </div>
        <button
          onClick={downloadPdf}
          disabled={downloading}
          data-testid="toolkit-download-pdf"
          className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {downloading ? "Preparing…" : "Download PDF"}
        </button>
        <button
          onClick={() => setShareOpen(true)}
          data-testid="toolkit-share-btn"
          className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2"
        >
          <Share2 size={14} /> Share with therapist
        </button>
      </div>

      <p className="text-neutral-400 leading-relaxed mb-6 max-w-2xl">{toolkit.description}</p>

      {/* Progress bar */}
      <div className="mb-8" data-testid="toolkit-progress">
        <div className="flex justify-between text-xs mb-2">
          <span className="text-neutral-500 uppercase tracking-widest">Progress</span>
          <span className="text-teal-300" data-testid="toolkit-progress-pct">
            {progress?.sections_read?.length || 0} of {progress?.total_sections || toolkit.sections.length} · {pct}%
          </span>
        </div>
        <div className="h-1.5 bg-teal-500/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-teal-400"
            style={{ width: `${pct}%`, transition: "width 400ms ease-out" }}
          />
        </div>
      </div>

      {toolkit.disclaimer && (
        <div className="mb-12 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100/90 leading-relaxed" data-testid="toolkit-disclaimer">
          {toolkit.disclaimer}
        </div>
      )}

      {/* Story Track — music paired with this toolkit (top) */}
      <div className="mb-10">
        <SuggestedTracks toolkitId={id} />
      </div>

      <div className="space-y-4">
        {toolkit.sections.map((s, i) => {
          const isRead = sectionsRead.has(i);
          return (
            <div
              key={i}
              ref={(el) => (sectionRefs.current[i] = el)}
              className="card-surface p-8"
              data-testid={`toolkit-section-${i}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-teal-300/80">Section {i + 1}</div>
                {isRead && (
                  <span className="text-xs text-teal-300 inline-flex items-center gap-1" data-testid={`section-complete-${i}`}>
                    <Check size={12} /> Read
                  </span>
                )}
              </div>
              <h2 className="font-display text-2xl mb-4">{s.title}</h2>
              <p className="text-neutral-200 leading-relaxed whitespace-pre-line">{s.body}</p>

              <ReflectionField
                toolkitId={id}
                index={i}
                initial={reflections[String(i)]?.text}
                onSaved={(idx, text) => setReflections((prev) => ({ ...prev, [String(idx)]: { text } }))}
              />

              {!isRead && (
                <button
                  onClick={() => markRead(i)}
                  data-testid={`section-mark-read-${i}`}
                  className="btn-ghost mt-6 px-4 py-2 text-xs inline-flex items-center gap-2"
                >
                  <Check size={14} /> Mark section complete
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-16 text-center text-sm text-neutral-500">
        You've reached the end of this toolkit. Come back to any section whenever the weather changes.
      </div>

      {/* Story Track — closing music (bottom) */}
      <div className="mt-10">
        <SuggestedTracks toolkitId={id} variant="compact" />
      </div>

      <ShareToolkitDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        toolkitId={id}
        toolkitTitle={toolkit.title}
      />
    </div>
  );
};

export default ToolkitViewer;
